import { useState } from 'react';
import { useSelectStep } from '~/routes/workflowStepParam';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useTasksDose } from '~/backend/connector-next-api/connector-next-components';
import { useApplyPredictedDoseSettings } from '~/hooks/dose/useApplyPredictedDoseSettings';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useConfirmedSelection from '~/hooks/datasetSelection/useConfirmedSelection';
import { useSnapshotInputApprovalStatus } from '~/hooks/workflows/useSnapshotInputApprovalStatus';
import { dosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';
import { OptimizationObjective } from '~/domain/patient/optimizationObjective';
import { Job } from '~/state/atoms/jobAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import {
    getHighestDoseQuantity,
    transformDoseTargets,
} from '~/utils/transforms/doseTargetsTransforms';
import { splitDicomAndJsonFiles } from '~/utils/io/files/FileHandler';
import {
    extractOptimizationObjectivesFile,
    readOptimizationObjectives,
} from '~/utils/transforms/optimizationObjectivesTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useCheckTaskInputs from '../useCheckTaskInputs';
import { handleTaskStartError, requireTaskId } from '../utils';

type UseDosePredictionTaskProps = {
    patientId: string;
    imageStudyId: string;
    imageSeriesId: string;
    structureSetInstanceUid: string;
};

/**
 * A hook for creating a dose prediction task via Connector API.
 * @param {UseDosePredictionTaskProps} props - Contains study and series identifiers for the task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useDosePredictionTask({
    patientId,
    imageStudyId,
    imageSeriesId,
    structureSetInstanceUid,
}: UseDosePredictionTaskProps) {
    const dosePredictionTaskOptions = useRecoilValue(dosePredictionTaskState);
    const predictObjectives = true;
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const selectStep = useSelectStep();

    const { mutateAsync: startDosePrediction } = useTasksDose();
    const checkTaskInputs = useCheckTaskInputs();
    const { addJob, updateJob } = useJobManagement();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { pickDose } = useConfirmedSelection();
    const applyPredictedDoseSettings = useApplyPredictedDoseSettings();
    const snapshotInputApprovalStatus = useSnapshotInputApprovalStatus();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            // Split JSON files from DICOM files so processCreatedFiles only
            // receives genuine DICOM data. Any extra JSONs from the worker
            // (beyond optimization_objectives.json) would otherwise cause
            // DICOM parsing to fail and the dose to not be rendered.
            const { dicomFiles, jsonFiles } = splitDicomAndJsonFiles(
                job.results
            );
            const objFile = extractOptimizationObjectivesFile(jsonFiles);
            let objectives: OptimizationObjective[] = [];
            if (objFile) {
                try {
                    objectives = await readOptimizationObjectives(objFile);
                } catch (e) {
                    handleError(e, 'Failed to parse optimization objectives.');
                }
            }
            const generationModel = transformTaskToGenerationModel(job.task);
            const processedItems = await processCreatedFiles(
                dicomFiles,
                generationModel
            );
            if (processedItems) {
                updateJob(job.id, { isImported: true });
                const taskTargets =
                    job.task && 'targets' in job.task
                        ? job.task.targets
                        : undefined;
                applyPredictedDoseSettings(processedItems.doses, {
                    objectives,
                    targets: taskTargets,
                    normalizationValueGy: taskTargets
                        ? getHighestDoseQuantity(taskTargets)
                        : undefined,
                });
                // The result loads through the same question as a picked
                // dose: the user may have changed the set while the job ran.
                if (processedItems.doses[0]) {
                    pickDose(processedItems.doses[0]);
                }
            }
        }
    };
    /**
     * Creates the dose prediction task with the provided model.
     * @param {string} modelName - The model to use for the dose prediction task.
     */
    const start = async (modelName: string) => {
        if (isSubmitting) {
            return;
        }
        if (!dosePredictionTaskOptions?.targets) {
            handleError(new Error('No target defined.'));
            return;
        }

        setIsSubmitting(true);
        const inputApprovalStatus = await snapshotInputApprovalStatus(
            structureSetInstanceUid
        );
        try {
            const inputs = await checkTaskInputs({
                patientId,
                seriesInstanceUids: [imageSeriesId],
                structureSetSopInstanceUids: [structureSetInstanceUid],
            });

            const task = await startDosePrediction({
                body: {
                    modelName,
                    imageSeries: {
                        patientID: patientId,
                        seriesInstanceUID: imageSeriesId,
                        studyInstanceUID: imageStudyId,
                    },
                    structureSet: {
                        sopInstanceUID: inputs.storedStructureSetUid(
                            structureSetInstanceUid
                        ),
                        patientID: patientId,
                    },
                    doseTargets: transformDoseTargets(
                        dosePredictionTaskOptions.targets
                    ),
                    predictObjectives: predictObjectives,
                },
            });

            const newJob: NewJobData = {
                name: `Dose+`,
                id: requireTaskId(task),
                type: WorkflowTypes.DOSE,
                task: {
                    targets: dosePredictionTaskOptions.targets,
                    model: dosePredictionTaskOptions.model,
                    output: task,
                },
                onLoad: handleProcessedFiles,
                inputApprovalStatus,
            };

            addJob(newJob);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));

            // The step, not a route: the context the user is in stays as it is.
            selectStep('predictive-planning');
        } catch (error) {
            handleTaskStartError(
                error,
                'Failed to start dose prediction task.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return { start, isSubmitting };
}
